/**
 * 多图参考(W2-B9,2026-09-25 模特试戴)—— 纯函数层。
 *
 * 0923 实测:中转 `/images/edits` + gpt-image-1 + input_fidelity 吃多张参考图
 * (产品白底图 + 模特图 → 模特原样、产品戴上)。三仓契约:
 *   createTask.refImageRefs = [{ ref: objectKey, role: product|model|scene|style }],≤4 张;
 *   与 coverImageRef(底图)可共存;**只收 1K**(保真参数只在 1K 真送达,api / ai 都校);
 *   不与组图 / 多比例套图 / 圈选组合;报价按 1K 单价,参考图不另计费。
 *
 * ★图序由 ai 侧按 role 固定顺序排(产品→模特→场景→风格),前端不排序、不重复实现。
 */
import type { ResolutionTier } from "../../api/posterService";

export type RefImageRole = "product" | "model" | "scene" | "style";

export const MAX_REF_IMAGES = 4;

/** role 下拉的选项(顺序即 ai 侧图序)。 */
export const REF_IMAGE_ROLES: ReadonlyArray<{ value: RefImageRole; label: string; hint: string }> =
  [
    { value: "product", label: "产品", hint: "外形、材质、颜色照这张来,不改款" },
    { value: "model", label: "模特", hint: "保持模特的脸、发型、服装不变" },
    { value: "scene", label: "场景", hint: "背景环境与氛围参照这张" },
    { value: "style", label: "风格", hint: "只参照色调、光影与画面风格" },
  ];

export interface MultiRefImage {
  objectKey: string;
  previewUrl: string;
  role: RefImageRole;
}

export function isRefImageRole(v: string): v is RefImageRole {
  return REF_IMAGE_ROLES.some((r) => r.value === v);
}

export function canAddRefImage(list: ReadonlyArray<MultiRefImage>): boolean {
  return list.length < MAX_REF_IMAGES;
}

/** 追加一张;已满 4 张时原样返回(调用方据 canAddRefImage 提示,这里是兜底)。 */
export function addRefImage(
  list: ReadonlyArray<MultiRefImage>,
  item: MultiRefImage
): MultiRefImage[] {
  return canAddRefImage(list) ? [...list, item] : [...list];
}

export function setRefImageRole(
  list: ReadonlyArray<MultiRefImage>,
  index: number,
  role: RefImageRole
): MultiRefImage[] {
  return list.map((r, i) => (i === index ? { ...r, role } : r));
}

export function removeRefImage(list: ReadonlyArray<MultiRefImage>, index: number): MultiRefImage[] {
  return list.filter((_, i) => i !== index);
}

/**
 * 新加一张默认给什么用途:还没有产品图 → 产品;有产品没模特 → 模特;否则场景。
 * ★底图已是产品实拍(商品主图 / 套图带实拍)时,产品已经在第 1 张了 —— 从模特起排,
 *   否则用户顺手传的模特照会被默认标成「产品」(1020 用户:上下两个框分不清)。
 */
export function defaultRoleFor(
  list: ReadonlyArray<MultiRefImage>,
  opts: { baseIsProduct?: boolean } = {}
): RefImageRole {
  if (!opts.baseIsProduct && !list.some((r) => r.role === "product")) return "product";
  if (!list.some((r) => r.role === "model")) return "model";
  return "scene";
}

/** 多图参考只收 1K:有参考图时提交/报价一律按 1K(api `PosterRefImages` 同规则)。 */
export function tierWithRefImages(
  tier: ResolutionTier,
  list: ReadonlyArray<MultiRefImage>
): ResolutionTier {
  return list.length > 0 ? "1K" : tier;
}

/** 某档在有参考图时能不能选(2K/4K 置灰)。 */
export function tierAllowedWithRefImages(tier: ResolutionTier, hasRefImages: boolean): boolean {
  return !hasRefImages || tier === "1K";
}

export const MULTI_REF_1K_ONLY_HINT = "多图参考仅支持 1K";

/**
 * 这一单的清晰度是否锁 1K;返回说给用户听的原因,null = 不锁。
 * 只有挂了多图参考才锁(api/ai 同样拒,保真参数只在 1K 档生效)。
 * ★2026-10-21 撤掉 1020 加的「GPT 系改图只给 1K」:后端改图按 2K/4K 下单,中转命中订阅号
 *   只交约 1.6M 时,结算按实交档降价(gate.record_delivered_tier),选项照常标,
 *   ⚙ 里那条黄字说明上游不保证。
 */
export function only1KReason(args: { hasRefImages: boolean }): string | null {
  return args.hasRefImages ? MULTI_REF_1K_ONLY_HINT : null;
}

/**
 * 当前形态能不能带参考图(组图 / 多比例套图各有自己的锚,ai 与 api 都会拒)。
 * 返回 null = 可以;否则是说给用户听的原因。
 */
export function refImagesBlockedReason(args: {
  groupMode: boolean;
  ratioSetMode: boolean;
}): string | null {
  if (args.groupMode) return "组图模式不支持参考图,本次不会带上";
  if (args.ratioSetMode) return "多比例套图不支持参考图,本次不会带上";
  return null;
}

/** 提交载荷:空 → undefined(不发这个键,存量行为一字不变)。 */
export function toRefImageRefs(
  list: ReadonlyArray<MultiRefImage>
): Array<{ ref: string; role: RefImageRole }> | undefined {
  if (list.length === 0) return undefined;
  return list.slice(0, MAX_REF_IMAGES).map((r) => ({ ref: r.objectKey, role: r.role }));
}

/** 草稿里存的多图参考(只存 objectKey + 用途;预览 URL 是临时签名,恢复时按 key 重签)。 */
export interface DraftRefImage {
  objectKey: string;
  role: RefImageRole;
}

/** 存草稿用:去掉临时预览 URL。 */
export function toDraftRefImages(list: ReadonlyArray<MultiRefImage>): DraftRefImage[] {
  return list.slice(0, MAX_REF_IMAGES).map((r) => ({ objectKey: r.objectKey, role: r.role }));
}

/** 读草稿用:逐项验型,形状不对的项丢掉(陈年/被写坏的 formJson 不带病进表单),最多 4 张。 */
export function parseDraftRefImages(raw: unknown): DraftRefImage[] {
  if (!Array.isArray(raw)) return [];
  const out: DraftRefImage[] = [];
  for (const it of raw) {
    if (!it || typeof it !== "object") continue;
    const { objectKey, role } = it as { objectKey?: unknown; role?: unknown };
    if (typeof objectKey !== "string" || !objectKey) continue;
    if (typeof role !== "string" || !isRefImageRole(role)) continue;
    out.push({ objectKey, role });
    if (out.length >= MAX_REF_IMAGES) break;
  }
  return out;
}
